import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserProfile, updateHealthPreferences } from '../../../services/profileService';

const HealthPreferencesSection = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    dailyStepsGoal: 10000,
    dailyCaloriesGoal: 2000,
    dailyWaterGoal: 8,
    sleepGoal: 8,
    activityLevel: 'moderate',
    dietaryPreferences: [],
    healthConditions: [],
    macroTargets: { protein: 25, carbs: 50, fat: 25 }
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const profile = await getUserProfile(user?.id);
      
      if (profile) {
        setPreferences({
          dailyStepsGoal: profile?.dailyStepsGoal || 10000,
          dailyCaloriesGoal: profile?.dailyCaloriesGoal || 2000,
          dailyWaterGoal: profile?.dailyWaterGoal || 8,
          sleepGoal: profile?.sleepGoal || 8,
          activityLevel: profile?.activityLevel || 'moderate',
          dietaryPreferences: profile?.dietaryPreferences || [],
          healthConditions: profile?.healthConditions || [],
          macroTargets: profile?.macroTargets || { protein: 25, carbs: 50, fat: 25 }
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSelectChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleMultiSelectChange = (key, option) => {
    setPreferences(prev => {
      const currentArray = prev?.[key];
      const newArray = currentArray?.includes(option)
        ? currentArray?.filter(item => item !== option)
        : [...currentArray, option];
      return { ...prev, [key]: newArray };
    });
    setHasChanges(true);
  };

  // Macro ratio handler: adjusts the other two proportionally to keep sum = 100
  const handleMacroChange = (macro, newValue) => {
    const val = Math.max(5, Math.min(90, parseInt(newValue) || 0));
    setPreferences(prev => {
      const current = prev?.macroTargets;
      const others = ['protein', 'carbs', 'fat']?.filter(k => k !== macro);
      const remaining = 100 - val;
      const otherSum = current?.[others?.[0]] + current?.[others?.[1]];
      let a, b;
      if (otherSum === 0) {
        a = Math.round(remaining / 2);
        b = remaining - a;
      } else {
        a = Math.round((current?.[others?.[0]] / otherSum) * remaining);
        b = remaining - a;
      }
      return {
        ...prev,
        macroTargets: {
          ...current,
          [macro]: val,
          [others?.[0]]: Math.max(5, a),
          [others?.[1]]: Math.max(5, b)
        }
      };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);
      const result = await updateHealthPreferences(user?.id, preferences);
      
      if (result?.success) {
        setHasChanges(false);
      } else {
        alert('Failed to save health preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save health preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadPreferences();
    setHasChanges(false);
  };

  const activityLevels = [
    { value: 'sedentary', label: 'Sedentary', description: 'Little to no exercise' },
    { value: 'light', label: 'Light', description: 'Light exercise 1-3 days/week' },
    { value: 'moderate', label: 'Moderate', description: 'Moderate exercise 3-5 days/week' },
    { value: 'active', label: 'Active', description: 'Hard exercise 6-7 days/week' },
    { value: 'very-active', label: 'Very Active', description: 'Very hard exercise, physical job' }
  ];

  const dietaryOptions = [
    'vegetarian', 'vegan', 'pescatarian', 'keto', 'paleo', 'mediterranean', 'low-carb', 'gluten-free'
  ];

  const healthConditionOptions = [
    'diabetes', 'hypertension', 'heart-disease', 'arthritis', 'asthma', 'thyroid', 'none'
  ];

  const macroTotal = (preferences?.macroTargets?.protein || 0) + (preferences?.macroTargets?.carbs || 0) + (preferences?.macroTargets?.fat || 0);
  const macroValid = macroTotal === 100;

  const macroConfig = [
    { key: 'protein', label: 'Protein', color: '#10B981', bgColor: 'bg-emerald-500', trackBg: 'bg-emerald-100 dark:bg-emerald-900/30', textColor: 'text-emerald-600 dark:text-emerald-400', description: 'Muscle repair & growth' },
    { key: 'carbs', label: 'Carbs', color: '#3B82F6', bgColor: 'bg-blue-500', trackBg: 'bg-blue-100 dark:bg-blue-900/30', textColor: 'text-blue-600 dark:text-blue-400', description: 'Energy & performance' },
    { key: 'fat', label: 'Fat', color: '#F59E0B', bgColor: 'bg-amber-500', trackBg: 'bg-amber-100 dark:bg-amber-900/30', textColor: 'text-amber-600 dark:text-amber-400', description: 'Hormones & absorption' }
  ];

  const macroPresets = [
    {
      id: 'muscle-gain',
      label: 'Muscle Gain',
      icon: 'Dumbbell',
      description: 'High protein for muscle growth',
      values: { protein: 35, carbs: 45, fat: 20 },
      color: 'text-emerald-600 dark:text-emerald-400',
      activeBg: 'bg-emerald-500/10 border-emerald-500',
      hoverBg: 'hover:border-emerald-400/50 hover:bg-emerald-500/5'
    },
    {
      id: 'fat-loss',
      label: 'Fat Loss',
      icon: 'Flame',
      description: 'Low carb, high protein',
      values: { protein: 40, carbs: 25, fat: 35 },
      color: 'text-orange-600 dark:text-orange-400',
      activeBg: 'bg-orange-500/10 border-orange-500',
      hoverBg: 'hover:border-orange-400/50 hover:bg-orange-500/5'
    },
    {
      id: 'balanced',
      label: 'Balanced',
      icon: 'Scale',
      description: 'Balanced macros for maintenance',
      values: { protein: 25, carbs: 50, fat: 25 },
      color: 'text-blue-600 dark:text-blue-400',
      activeBg: 'bg-blue-500/10 border-blue-500',
      hoverBg: 'hover:border-blue-400/50 hover:bg-blue-500/5'
    }
  ];

  const getActivePreset = () => {
    const mt = preferences?.macroTargets;
    return macroPresets?.find(p =>
      p?.values?.protein === mt?.protein &&
      p?.values?.carbs === mt?.carbs &&
      p?.values?.fat === mt?.fat
    )?.id || null;
  };

  const handleApplyPreset = (preset) => {
    setPreferences(prev => ({
      ...prev,
      macroTargets: { ...preset?.values }
    }));
    setHasChanges(true);
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-secondary rounded-lg">
            <Icon name="Target" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Health Preferences</h2>
            <p className="text-sm text-muted-foreground">Customize your wellness goals and preferences</p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleReset} size="sm">
              Reset
            </Button>
            <Button variant="default" onClick={handleSave} size="sm" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-8">
        {/* Daily Goals */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Daily Goals</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Steps Goal */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Daily Steps</label>
                <span className="text-sm font-semibold text-primary">{preferences?.dailyStepsGoal?.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="5000"
                max="20000"
                step="500"
                value={preferences?.dailyStepsGoal}
                onChange={(e) => handleSliderChange('dailyStepsGoal', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>5,000</span>
                <span>20,000</span>
              </div>
            </div>

            {/* Calories Goal */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Daily Calories</label>
                <span className="text-sm font-semibold text-primary">{preferences?.dailyCaloriesGoal}</span>
              </div>
              <input
                type="range"
                min="1200"
                max="3500"
                step="50"
                value={preferences?.dailyCaloriesGoal}
                onChange={(e) => handleSliderChange('dailyCaloriesGoal', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1,200</span>
                <span>3,500</span>
              </div>
            </div>

            {/* Water Goal */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Daily Water (glasses)</label>
                <span className="text-sm font-semibold text-primary">{preferences?.dailyWaterGoal}</span>
              </div>
              <input
                type="range"
                min="4"
                max="16"
                step="1"
                value={preferences?.dailyWaterGoal}
                onChange={(e) => handleSliderChange('dailyWaterGoal', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>4</span>
                <span>16</span>
              </div>
            </div>

            {/* Sleep Goal */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Sleep Goal (hours)</label>
                <span className="text-sm font-semibold text-primary">{preferences?.sleepGoal}</span>
              </div>
              <input
                type="range"
                min="6"
                max="10"
                step="0.5"
                value={preferences?.sleepGoal}
                onChange={(e) => handleSliderChange('sleepGoal', parseFloat(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>6h</span>
                <span>10h</span>
              </div>
            </div>
          </div>
        </div>

        {/* Macro Ratio Targets */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-foreground">Macro Ratio Targets</h3>
              <p className="text-sm text-muted-foreground">Set your daily macronutrient distribution — must total 100%</p>
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
              macroValid ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
            }`}>
              <Icon name={macroValid ? 'CheckCircle2' : 'AlertCircle'} size={13} />
              {macroTotal}% total
            </div>
          </div>

          {/* Quick Preset Buttons */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {macroPresets?.map(preset => {
              const isActive = getActivePreset() === preset?.id;
              return (
                <button
                  key={preset?.id}
                  onClick={() => handleApplyPreset(preset)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 text-center ${
                    isActive
                      ? preset?.activeBg
                      : `border-border bg-card ${preset?.hoverBg}`
                  }`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isActive ? 'bg-current/10' : 'bg-muted'}`}>
                    <Icon name={preset?.icon} size={16} className={isActive ? preset?.color : 'text-muted-foreground'} />
                  </div>
                  <span className={`text-xs font-semibold leading-tight ${isActive ? preset?.color : 'text-foreground'}`}>
                    {preset?.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground leading-tight hidden sm:block">
                    P{preset?.values?.protein} / C{preset?.values?.carbs} / F{preset?.values?.fat}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Visual bar showing distribution */}
          <div className="flex h-4 rounded-full overflow-hidden mb-6 gap-0.5">
            {macroConfig?.map(m => (
              <div
                key={m?.key}
                className="transition-all duration-300 rounded-sm"
                style={{ width: `${preferences?.macroTargets?.[m?.key] || 0}%`, backgroundColor: m?.color }}
                title={`${m?.label}: ${preferences?.macroTargets?.[m?.key]}%`}
              />
            ))}
          </div>

          <div className="space-y-5">
            {macroConfig?.map(m => (
              <div key={m?.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m?.color }} />
                    <label className="text-sm font-medium text-foreground">{m?.label}</label>
                    <span className="text-xs text-muted-foreground hidden sm:inline">— {m?.description}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="5"
                      max="90"
                      value={preferences?.macroTargets?.[m?.key] || 0}
                      onChange={(e) => handleMacroChange(m?.key, e?.target?.value)}
                      className="w-16 text-center text-sm font-semibold bg-muted border border-border rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <span className={`text-sm font-bold w-6 ${m?.textColor}`}>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="5"
                  max="90"
                  step="1"
                  value={preferences?.macroTargets?.[m?.key] || 0}
                  onChange={(e) => handleMacroChange(m?.key, e?.target?.value)}
                  className={`w-full h-2 rounded-lg appearance-none cursor-pointer macro-slider-${m?.key}`}
                  style={{ background: `linear-gradient(to right, ${m?.color} 0%, ${m?.color} ${preferences?.macroTargets?.[m?.key]}%, var(--color-muted) ${preferences?.macroTargets?.[m?.key]}%, var(--color-muted) 100%)` }}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5%</span>
                  <span className={`font-medium ${m?.textColor}`}>
                    ~{Math.round((preferences?.dailyCaloriesGoal * (preferences?.macroTargets?.[m?.key] / 100)) / (m?.key === 'fat' ? 9 : 4))}g / day
                  </span>
                  <span>90%</span>
                </div>
              </div>
            ))}
          </div>

          {!macroValid && (
            <div className="mt-3 flex items-center gap-2 text-xs text-destructive bg-destructive/5 rounded-lg px-3 py-2">
              <Icon name="AlertCircle" size={13} />
              Adjust values so they add up to exactly 100%
            </div>
          )}
        </div>

        {/* Activity Level */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Activity Level</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activityLevels?.map((level) => (
              <div
                key={level?.value}
                onClick={() => handleSelectChange('activityLevel', level?.value)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  preferences?.activityLevel === level?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    preferences?.activityLevel === level?.value
                      ? 'border-primary bg-primary' :'border-border'
                  }`}>
                    {preferences?.activityLevel === level?.value && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                  <span className="font-medium text-foreground">{level?.label}</span>
                </div>
                <p className="text-sm text-muted-foreground">{level?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Dietary Preferences */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Dietary Preferences</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {dietaryOptions?.map((option) => (
              <div
                key={option}
                onClick={() => handleMultiSelectChange('dietaryPreferences', option)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                  preferences?.dietaryPreferences?.includes(option)
                    ? 'border-secondary bg-secondary/5' :'border-border hover:border-secondary/50'
                }`}
              >
                <span className="text-sm font-medium text-foreground capitalize">
                  {option?.replace('-', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Health Conditions */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Health Conditions</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Select any health conditions to help us provide better recommendations
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {healthConditionOptions?.map((condition) => (
              <div
                key={condition}
                onClick={() => handleMultiSelectChange('healthConditions', condition)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                  preferences?.healthConditions?.includes(condition)
                    ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                }`}
              >
                <span className="text-sm font-medium text-foreground capitalize">
                  {condition?.replace('-', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[class*="macro-slider"]::-webkit-slider-thumb {
          appearance: none;
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid currentColor;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        input[class*="macro-slider"]::-moz-range-thumb {
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid currentColor;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default HealthPreferencesSection;